<template>
	<li
		class="chat-row"
		:class="{ 'chat-row--unread': unread }"
		:style="rowStyle"
		role="button"
		tabindex="0"
		:aria-label="ariaLabel"
		@click="$emit('open', chat)"
		@keydown.enter.prevent="$emit('open', chat)"
		@keydown.space.prevent="$emit('open', chat)"
	>
		<span class="chat-row__rail" :style="{ backgroundColor: visual.accent }" />
		<span class="chat-row__avatar">
			<channel-avatar :type-id="chat.typeId" :image="chat.counterpart.image" :size="40" />
			<i class="chat-row__corner" :class="{ 'chat-row__corner--count': unread }" :style="cornerStyle">
				<template v-if="unread">{{ unreadLabel }}</template>
				<chat-icon v-else :name="visual.icon" :size="12" />
			</i>
		</span>
		<div class="chat-row__main">
			<div class="chat-row__line">
				<span class="chat-row__name">{{ title }}</span>
				<span
					v-if="named"
					class="chat-row__tag"
					:style="{ backgroundColor: visual.tint, color: visual.accent }"
				>
					{{ channelLabel }}
				</span>
				<time
					class="chat-row__time"
					:datetime="chat.lastActivity"
					:style="unread ? { color: visual.accent } : null"
				>
					{{ time }}
				</time>
			</div>
			<div class="chat-row__meta">
				<span class="chat-row__meta-item">{{ orderLabel }}</span>
				<template v-if="businessName">
					<span class="chat-row__separator">|</span>
					<span class="chat-row__meta-item chat-row__business">
						<chat-icon name="store" :size="12" />
						<span class="chat-row__clip">{{ businessName }}</span>
					</span>
				</template>
			</div>
			<p class="chat-row__preview">
				<template v-if="chat.othersTyping">
					<span class="chat-row__typing" :style="{ color: visual.accent }">{{ $t('chat.typing') }}</span>
					<span class="chat-row__dots">
						<i
							v-for="dot in 3"
							:key="dot"
							class="chat-row__dot"
							:style="{ backgroundColor: visual.accent }"
						/>
					</span>
				</template>
				<template v-else-if="last">
					<chat-icon
						v-if="mine"
						:name="read ? 'done-all' : 'check'"
						:size="15"
						class="chat-row__tick"
						:class="{ 'chat-row__tick--read': read }"
					/>
					<chat-icon v-if="last.imageUrl" name="image" :size="14" class="chat-row__photo-icon" />
					<span class="chat-row__clip">{{ preview }}</span>
				</template>
				<span v-else class="chat-row__empty">{{ $t('chat.empty') }}</span>
			</p>
		</div>
	</li>
</template>

<script>
	import {
		CHAT_ORDER_TYPE_KEYS,
		CHAT_PAYMENT_KEYS,
		GENERAL_ORDER_CODE,
		chatChannelLabelKey,
		chatOrderCurrency,
		chatDisplayName,
		unreadBadgeLabel,
	} from '@/services/chat/channels';
	import { isMine } from '@/services/chat/chatFirestore';
	import UtilityService from '@/services/UtilityService';
	import ChannelAvatar from './ChannelAvatar.vue';
	import ChatIcon from './ChatIcon.vue';
	import { chatChannelVisual } from './chatVisuals';

	const STAGGER_MS = 28;
	const STAGGER_CAP = 14;

	export default {
		name: 'ChatRow',
		components: { ChannelAvatar, ChatIcon },
		props: {
			chat: {
				type: Object,
				required: true,
			},
			index: {
				type: Number,
				default: 0,
			},
			showBusiness: {
				type: Boolean,
				default: false,
			},
			restaurants: {
				type: Array,
				default: () => [],
			},
		},
		emits: ['open'],
		computed: {
			visual() {
				return chatChannelVisual(this.chat.typeId);
			},
			channelLabel() {
				return this.$t(chatChannelLabelKey(this.chat.typeId));
			},
			named() {
				return !!this.chat.counterpart.name;
			},
			title() {
				return chatDisplayName(this.$t, this.chat.typeId, this.chat.counterpart);
			},
			unread() {
				return this.chat.unreadCount > 0;
			},
			unreadLabel() {
				return unreadBadgeLabel(this.chat.unreadCount);
			},
			last() {
				return this.chat.lastMessage;
			},
			mine() {
				return !!this.last && isMine(this.last);
			},
			read() {
				return this.mine && this.last.readStatus === 1;
			},
			preview() {
				const text = this.last.text || this.$t('chat.list_photo');
				return this.mine ? `${this.$t('chat.list_you')}: ${text}` : text;
			},
			time() {
				return UtilityService.chatTimeLabel(this.chat.lastActivity);
			},
			// The order the chat was opened from, not its internal code: total, delivery or takeaway, payment.
			orderLabel() {
				if (this.chat.orderCode === GENERAL_ORDER_CODE) {
					return this.$t('chat.general_chat');
				}
				const { total, type, payment } = this.chat.order || {};
				const currency = chatOrderCurrency(this.restaurants, this.chat.businessName);
				const parts = [
					total > 0 ? UtilityService.filters.formatCurrency(total, currency) : null,
					CHAT_ORDER_TYPE_KEYS[type] ? this.$t(CHAT_ORDER_TYPE_KEYS[type]) : null,
					CHAT_PAYMENT_KEYS[payment] ? this.$t(CHAT_PAYMENT_KEYS[payment]) : null,
				].filter(Boolean);
				return parts.length ? parts.join(' · ') : this.$t('chat.list_order');
			},
			businessName() {
				return this.showBusiness ? this.chat.businessName : '';
			},
			rowStyle() {
				return {
					animationDelay: `${Math.min(this.index, STAGGER_CAP) * STAGGER_MS}ms`,
					backgroundColor: this.unread ? this.visual.tint : null,
				};
			},
			cornerStyle() {
				return { backgroundColor: this.visual.accent };
			},
			ariaLabel() {
				return [
					this.title,
					this.named ? this.channelLabel : null,
					this.orderLabel,
					this.businessName,
					this.unread ? this.$t('chat.list_unread', { count: this.chat.unreadCount }) : null,
					this.chat.othersTyping ? this.$t('chat.typing') : this.last ? this.preview : this.$t('chat.empty'),
				]
					.filter(Boolean)
					.join(', ');
			},
		},
	};
</script>

<style lang="scss" scoped>
	$ink: #1a202c;
	$text: #4a5568;
	$muted: #718096;
	$line: #e2e8f0;

	.chat-row {
		position: relative;
		display: grid;
		grid-template-columns: 6px 42px minmax(0, 1fr);
		column-gap: 10px;
		align-items: center;
		padding: 8px 12px 8px 0;
		background-color: var(--white);
		border-bottom: 1px solid $line;
		cursor: pointer;
		outline: none;
		transition: background-color 0.15s ease;
		animation: chat-row-rise 0.32s cubic-bezier(0.2, 0.8, 0.2, 1) both;

		&:hover,
		&:focus-visible {
			background-color: #f7fafc !important;
		}
	}

	.chat-row__rail {
		align-self: stretch;
		width: 3px;
		margin-left: 1px;
		border-radius: 0 6px 6px 0;
		opacity: 0.75;
	}

	.chat-row__avatar {
		position: relative;
		display: flex;
		width: 42px;
	}

	.chat-row__corner {
		position: absolute;
		top: -6px;
		left: -6px;
		display: flex;
		align-items: center;
		justify-content: center;
		width: 20px;
		height: 20px;
		font-style: normal;
		color: var(--white);
		border: 2px solid var(--white);
		border-radius: 9999px;
		box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
	}

	.chat-row__corner--count {
		width: auto;
		min-width: 22px;
		padding: 0 5px;
		font-size: 10px;
		font-weight: 800;
		line-height: 1;
		font-variant-numeric: tabular-nums;
	}

	.chat-row__main {
		min-width: 0;
	}

	.chat-row__line {
		display: flex;
		align-items: center;
		gap: 6px;
	}

	.chat-row__name {
		min-width: 0;
		overflow: hidden;
		font-size: 13px;
		font-weight: 700;
		line-height: 1.2;
		color: $ink;
		white-space: nowrap;
		text-overflow: ellipsis;
	}

	.chat-row__tag {
		flex-shrink: 0;
		padding: 1px 6px;
		font-size: 9px;
		font-weight: 700;
		line-height: 14px;
		letter-spacing: 0.06em;
		text-transform: uppercase;
		border-radius: 9999px;
	}

	.chat-row__time {
		flex-shrink: 0;
		margin-left: auto;
		padding-left: 4px;
		font-size: 10px;
		color: $muted;
		font-variant-numeric: tabular-nums;
	}

	.chat-row--unread .chat-row__time {
		font-weight: 700;
	}

	.chat-row__meta {
		display: flex;
		align-items: center;
		gap: 6px;
		min-width: 0;
		margin-top: 1px;
		font-size: 10px;
		font-weight: 600;
		line-height: 1.3;
		color: $muted;
	}

	.chat-row__meta-item {
		min-width: 0;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
	}

	.chat-row__business {
		display: inline-flex;
		align-items: center;
		gap: 3px;
	}

	.chat-row__clip {
		min-width: 0;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
	}

	.chat-row__separator {
		opacity: 0.6;
	}

	.chat-row__preview {
		display: flex;
		align-items: center;
		gap: 4px;
		min-width: 0;
		margin-top: 2px;
		font-size: 12px;
		line-height: 1.35;
		color: $text;
	}

	.chat-row--unread .chat-row__preview {
		font-weight: 600;
		color: $ink;
	}

	.chat-row__tick {
		color: $muted;
	}

	.chat-row__tick--read {
		color: var(--success);
	}

	.chat-row__photo-icon {
		color: $muted;
	}

	.chat-row__empty {
		font-style: italic;
		color: $muted;
	}

	.chat-row__typing {
		font-weight: 600;
		font-style: italic;
	}

	.chat-row__dots {
		display: inline-flex;
		gap: 3px;
	}

	.chat-row__dot {
		width: 4px;
		height: 4px;
		border-radius: 9999px;
		animation: chat-row-bounce 1.1s infinite ease-in-out;

		&:nth-child(2) {
			animation-delay: 0.15s;
		}

		&:nth-child(3) {
			animation-delay: 0.3s;
		}
	}

	@keyframes chat-row-rise {
		from {
			opacity: 0;
			transform: translateY(6px);
		}
		to {
			opacity: 1;
			transform: none;
		}
	}

	@keyframes chat-row-bounce {
		0%,
		80%,
		100% {
			transform: translateY(0);
			opacity: 0.5;
		}
		40% {
			transform: translateY(-3px);
			opacity: 1;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.chat-row,
		.chat-row__dot {
			animation: none;
		}
	}
</style>
